import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { api, ApiFail } from "./api/client";
import type { ImpersonationOut, MeResponse, OrgOut, UserOut } from "./api/types";
import { invoke, isDesktop, peekDesktopInfo } from "./desktop";

interface SessionState {
  user: UserOut | null;
  orgs: OrgOut[];
  /** Set only on a session `POST /auth/impersonate/consume` created (FR-AD7's banner). */
  impersonation: ImpersonationOut | null;
  /** True until the initial GET /auth/me resolves (success or failure). */
  loading: boolean;
  /** Re-fetches GET /auth/me and updates user/orgs. Call after signup/signin/org create. */
  refresh: () => Promise<void>;
  /** Optimistically sets the session after signup/signin, which already return {user}. */
  setUser: (user: UserOut) => void;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionState | undefined>(undefined);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<UserOut | null>(null);
  const [orgs, setOrgs] = useState<OrgOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [impersonation, setImpersonation] = useState<ImpersonationOut | null>(null);

  // ---- Preview mode: bypass API when URL has ?preview=1 (no backend needed) ----
  const refresh = useCallback(async () => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("preview") === "1" && !params.get("session") ) {
      // Seed mock user + orgs so the app renders for visual audit
      const mockUser: UserOut = {
        id: "550e8400-e29b-41d4-a716-446655440000",
        email: "wesley@grackle.app",
        name: "Wesley Hurd",
        email_verified: true,
        mfa_enabled: true,
        created_at: "2024-01-01T00:00:00Z",
      };
      const mockOrgs: OrgOut[] = [
        { id: "org-1", name: "Acme Design Co", slug: "acme-design", role: "owner", created_at: "2024-06-01T00:00:00Z" },
        { id: "org-2", name: "Grackle Internal", slug: "grackle", role: "admin", created_at: "2024-01-01T00:00:00Z" },
      ];
      setUserState(mockUser);
      setOrgs(mockOrgs);
      setLoading(false);
      return;
    }
    try {
      const me = await api<MeResponse>("GET", "/auth/me");
      setUserState(me.user);
      setOrgs(me.orgs);
      setImpersonation(me.impersonation);
    } catch (err) {
      // Unauthenticated is expected (anonymous visitor) — anything else we still treat as
      // "no session" for UI purposes; the failing request is enough of a signal on its own.
      if (!(err instanceof ApiFail)) {
        console.error("session refresh failed", err);
      }
      setUserState(null);
      setOrgs([]);
      setImpersonation(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setUser = useCallback((next: UserOut) => {
    setUserState(next);
  }, []);

  const signOut = useCallback(async () => {
    try {
      // FR-A4: a packaged desktop build with a configured API origin signs out through Rust
      // (server revoke + clear the keychain entry + drop the in-memory access token) rather
      // than this module's own cookie-based `POST /auth/signout` — there is no cookie to clear
      // and the access token this process holds is the Rust side's, not this one's.
      if (isDesktop() && peekDesktopInfo()?.api_origin) {
        await invoke("auth_sign_out");
      } else {
        await api<void>("POST", "/auth/signout");
      }
    } catch {
      // Best-effort — the session may already be gone. We still clear local state below.
    }
    setUserState(null);
    setOrgs([]);
    setImpersonation(null);
  }, []);

  const value = useMemo<SessionState>(
    () => ({ user, orgs, impersonation, loading, refresh, setUser, signOut }),
    [user, orgs, impersonation, loading, refresh, setUser, signOut],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const ctx = useContext(SessionContext);
  if (!ctx) {
    throw new Error("useSession must be used within a SessionProvider");
  }
  return ctx;
}
